import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { apiFetch } from "@/lib/api-client";
import { PropertyCard, BorrowerCard } from "@/components/loans/DealHead";
import { ComparisonCard } from "@/components/loans/ComparisonCard";
import { IcpCard } from "@/components/loans/IcpCard";
import { MailerOutputCard } from "@/components/loans/MailerOutputCard";
import { useTopbar } from "@/components/chrome/use-topbar";
import { TuneValuationSheet } from "@/components/loans/TuneValuationSheet";
import type { LoanRecord, DynamoTermsSettings } from "@/components/loans/DealHead";
import type { LendersResponse, MarketsResponse } from "@/lib/derive";
import type { RunRecord } from "@/components/loans/MailerOutputCard";

// ---------------------------------------------------------------------------
// API response shapes (inline — minimal, keyed to fields we read)
// ---------------------------------------------------------------------------

interface RunsResponse {
  rows: (RunRecord & { id: string; runId: string })[];
}

function StatusActions({
  isMailed,
  isSkipped,
  isMutating,
  onMailed,
  onSkip,
}: {
  isMailed: boolean;
  isSkipped: boolean;
  isMutating: boolean;
  onMailed: () => void;
  onSkip: () => void;
}) {
  return (
    <>
      <button
        type="button"
        disabled={isMutating || isMailed}
        onClick={onMailed}
        className="inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-gold px-3.5 py-2.5 text-xs font-semibold text-ink hover:bg-gold/90 disabled:cursor-not-allowed disabled:opacity-60 md:flex-none md:py-2"
      >
        {isMailed ? "Mailed" : "Mark mailed"}
      </button>
      <button
        type="button"
        disabled={isMutating || isSkipped}
        onClick={onSkip}
        className="inline-flex flex-1 items-center justify-center gap-2 rounded-md border border-rule-strong bg-surface px-3.5 py-2.5 text-xs font-semibold text-ink disabled:cursor-not-allowed disabled:opacity-60 md:flex-none md:py-2"
      >
        {isSkipped ? "Skipped" : "Skip"}
      </button>
    </>
  );
}

export const Route = createFileRoute("/_app/loans/$id/")({
  component: LoanDetailPage,
});

function LoanDetailPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const [tuning, setTuning] = useState(false);

  const loanQuery = useQuery({
    queryKey: ["loan", id],
    queryFn: () => apiFetch<{ loan: LoanRecord }>(`/api/loans/${id}`),
  });

  const dynamoTermsQuery = useQuery({
    queryKey: ["settings", "dynamo-terms"],
    queryFn: () => apiFetch<DynamoTermsSettings>("/api/settings/dynamo-terms"),
    staleTime: 60_000,
  });

  const lendersQuery = useQuery({
    queryKey: ["settings", "lenders"],
    queryFn: () => apiFetch<LendersResponse>("/api/settings/lenders"),
    staleTime: 60_000,
  });

  const marketsQuery = useQuery({
    queryKey: ["settings", "markets"],
    queryFn: () => apiFetch<MarketsResponse>("/api/settings/markets"),
    staleTime: 60_000,
  });

  const runsQuery = useQuery({
    queryKey: ["runs", "recent"],
    queryFn: () => apiFetch<RunsResponse>("/api/runs?limit=20"),
    staleTime: 60_000,
  });

  const setStatus = useMutation({
    mutationFn: (status: "awaiting_mail" | "mailed" | "skipped") =>
      apiFetch(`/api/loans/${id}/status`, {
        method: "PUT",
        body: JSON.stringify({ status }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["loan", id] }),
  });

  const { setTopbar } = useTopbar();
  const borrowerName = loanQuery.data?.loan.borrowerName ?? "";
  const loanStatus = loanQuery.data?.loan.status ?? null;
  const isMutating = setStatus.isPending;
  const setStatusMutate = setStatus.mutate;
  useEffect(() => {
    if (!borrowerName) return;
    const isMailed = loanStatus === "mailed";
    const isSkipped = loanStatus === "skipped";
    setTopbar({
      crumbs: [
        { label: "Opportunities", to: "/opportunities" },
        { label: "Loans", to: "/loans" },
        { label: borrowerName },
      ],
      rightSlot: (
        <StatusActions
          isMailed={isMailed}
          isSkipped={isSkipped}
          isMutating={isMutating}
          onMailed={() => setStatusMutate("mailed")}
          onSkip={() => setStatusMutate("skipped")}
        />
      ),
    });
    return () => setTopbar({ crumbs: [], rightSlot: null });
  }, [setTopbar, borrowerName, loanStatus, isMutating, setStatusMutate]);

  if (loanQuery.isLoading) {
    return <p className="text-sm text-muted">Loading…</p>;
  }
  if (loanQuery.error || !loanQuery.data) {
    return (
      <div className="rounded-lg border border-rule bg-surface px-8 py-10 text-center">
        <h2 className="mb-2 text-lg font-semibold text-ink">Loan not found</h2>
        <p className="mb-5 text-sm text-muted">
          This loan ID doesn&apos;t exist or you may not have access to it.
        </p>
        <Link
          to="/loans"
          search={{ q: "", sort: "delta", order: "desc", page: 1 }}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink hover:underline"
        >
          <span aria-hidden="true">←</span> Back to loans
        </Link>
      </div>
    );
  }

  const l = loanQuery.data.loan;
  const run = runsQuery.data?.rows?.find((r) => r.runId === l.runId) ?? null;

  // Markets API returns a bare array, not { markets: [...] }. Normalise it so
  // pickMarket always sees { markets: [...] }.
  const rawMarkets = marketsQuery.data;
  const marketsNorm: MarketsResponse | undefined = rawMarkets
    ? Array.isArray(rawMarkets)
      ? { markets: rawMarkets as MarketsResponse["markets"] }
      : rawMarkets
    : undefined;

  const mailedNow = l.status === "mailed";
  const skippedNow = l.status === "skipped";

  return (
    <div className="space-y-5 md:space-y-7">
      {/* Mobile-only action row (desktop uses the top bar) */}
      <div className="flex gap-2.5 md:hidden">
        <StatusActions
          isMailed={mailedNow}
          isSkipped={skippedNow}
          isMutating={setStatus.isPending}
          onMailed={() => setStatus.mutate("mailed")}
          onSkip={() => setStatus.mutate("skipped")}
        />
      </div>

      {/* Deal head: property + borrower */}
      <section className="grid grid-cols-1 gap-5 md:grid-cols-[minmax(0,1fr)_360px] md:items-stretch md:gap-7">
        <PropertyCard
          loan={l}
          dynamoTerms={dynamoTermsQuery.data}
          lenders={lendersQuery.data}
          onTune={() => setTuning(true)}
        />
        <BorrowerCard loan={l} />
      </section>

      {/* Comparison card */}
      <ComparisonCard loan={l} dynamoTerms={dynamoTermsQuery.data} />

      {/* Lower grid: ICP + Mailer */}
      <section className="grid grid-cols-1 gap-5 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] md:gap-7">
        <IcpCard loan={l} lenders={lendersQuery.data} markets={marketsNorm} />
        <MailerOutputCard
          loan={l}
          run={run}
          dynamoTerms={dynamoTermsQuery.data}
          onMarkMailed={() => setStatus.mutate("mailed")}
          onTune={() => setTuning(true)}
        />
      </section>

      {/* Disclaimer footer */}
      <p className="border-l-2 border-tan-dark bg-paper-2 px-4 py-3.5 text-[11px] leading-relaxed text-muted">
        <strong className="text-ink">Disclaimer.</strong> Re-underwriting values are estimates
        derived from ARV (after-repair value) and as-is AVM data (
        <code className="font-medium">sfr_get_property</code>) and Dynamo Capital&apos;s configured
        loan parameters at the time of generation. Numbers shown here are not a binding loan offer.
        Final disclaimer language pending legal review.
      </p>

      <TuneValuationSheet
        loan={l}
        dynamoTerms={dynamoTermsQuery.data}
        open={tuning}
        onClose={() => setTuning(false)}
      />
    </div>
  );
}
